import type {
  OrgIntelligenceProofLevelDto,
  OrgIntelligenceSourceArtifactRefDto,
} from './org-intelligence-ontology';
import type {
  VcbPayloadRedactionDto,
  VcbPermissionPostureDto,
  VcbSensitivityLevelDto,
} from './vcb-security';

export const VCB_DOMAIN_TOOL_KINDS = [
  'objective_lookup',
  'org_graph_read',
  'economic_claim_read',
  'evidence_read',
  'pressure_test_read',
  'supervision_write',
  'decision_pack_draft',
  'control_plan_preview',
] as const;

export type VcbDomainToolKindDto = (typeof VCB_DOMAIN_TOOL_KINDS)[number];

export type VcbDomainToolScopeDto = {
  tenantIdRequired: boolean;
  strategicObjectiveIdRequired: boolean;
  runIdRequired: boolean;
};

export type VcbDomainToolModelUseDto = {
  mayPlan: boolean;
  mayInvokeRead: boolean;
  mayDraftFromResult: boolean;
  mayWrite: boolean;
  mayActivate: boolean;
};

export type VcbDomainToolGuardrailsDto = {
  requiresHumanApproval: boolean;
  requiresReauthForDelayedExecution: boolean;
  promptInjectionResistant: boolean;
  blockedWhenRedactionUnavailable: boolean;
};

export type VcbDomainToolContractDto = {
  toolId: string;
  kind: VcbDomainToolKindDto;
  displayName: string;
  description: string;
  ownerService: string;
  inputSchemaRef: string;
  outputSchemaRef: string;
  scope: VcbDomainToolScopeDto;
  requiredPermissionKeys: string[];
  sensitivity: VcbSensitivityLevelDto;
  deterministicTruthOwner: boolean;
  modelUse: VcbDomainToolModelUseDto;
  guardrails: VcbDomainToolGuardrailsDto;
  auditEventKind: string;
};

export type VcbContextPackSourceTimestampDto = {
  sourceKind: string;
  sourceId: string;
  asOf: string;
  freshnessStatus: 'fresh' | 'stale' | 'missing' | 'unknown';
};

export type VcbContextPackDto = {
  contextPackId: string;
  tenantId: string;
  strategicObjectiveId: string;
  scopeKind: string;
  scopeId: string;
  generatedAt: string;
  expiresAt: string;
  sourceTimestamps: VcbContextPackSourceTimestampDto[];
  proofLabels: OrgIntelligenceProofLevelDto[];
  sensitivityLabels: VcbSensitivityLevelDto[];
  redactionSummary: VcbPayloadRedactionDto;
  permissionPosture: VcbPermissionPostureDto;
  sourceArtifactRefs: OrgIntelligenceSourceArtifactRefDto[];
  allowedToolIds: string[];
  blockedToolIds: string[];
  blockedReasons: string[];
};

export type VcbStructuredSynthesisStatusDto =
  | 'draft_not_governed'
  | 'blocked'
  | 'validation_failed'
  | 'ready_for_human_review';

export type VcbStructuredSynthesisPostureDto =
  | 'recommend'
  | 'recommend_with_gaps'
  | 'cannot_recommend_yet'
  | 'blocked'
  | 'needs_clarification';

export type VcbDeterministicValidationDto = {
  required: boolean;
  status: 'not_run' | 'passed' | 'failed';
  validatorService: string;
  failureReasons: string[];
};

export type VcbGovernanceBindingDto = {
  decisionLedgerDecisionId: string | null;
  synthesisSnapshotArtifactId: string | null;
  maySubmitForApproval: boolean;
};

export type VcbStructuredSynthesisOutputDto = {
  outputId: string;
  schemaVersion: string;
  contextPackId: string;
  status: VcbStructuredSynthesisStatusDto;
  posture: VcbStructuredSynthesisPostureDto;
  headline: string;
  executiveTakeaway: string;
  claimIds: string[];
  gapIds: string[];
  pressureTestItemIds: string[];
  deterministicTruthRefs: string[];
  redactionSummary: VcbPayloadRedactionDto;
  deterministicValidation: VcbDeterministicValidationDto;
  governanceBinding: VcbGovernanceBindingDto;
};

export type VcbAgentToolCallStatusDto = 'queued' | 'running' | 'succeeded' | 'blocked' | 'failed';

export type VcbAgentToolGuardrailDecisionDto =
  | 'allowed'
  | 'blocked_scope'
  | 'blocked_permission'
  | 'blocked_redaction'
  | 'blocked_policy'
  | 'requires_human_approval';

export type VcbAgentToolCallTraceDto = {
  toolCallId: string;
  toolId: string;
  inputHash: string;
  outputHash: string | null;
  status: VcbAgentToolCallStatusDto;
  guardrailDecision: VcbAgentToolGuardrailDecisionDto;
  startedAt: string;
  endedAt: string | null;
};

export type VcbStructuredOutputValidationDto = {
  schemaRef: string;
  status: 'passed' | 'failed' | 'not_run';
  errors: string[];
};

export type VcbAgentTraceDto = {
  traceId: string;
  tenantId: string;
  strategicObjectiveId: string;
  contextPackId: string;
  startedAt: string;
  endedAt: string | null;
  modelProvider: string;
  modelName: string;
  toolCalls: VcbAgentToolCallTraceDto[];
  structuredOutputValidation: VcbStructuredOutputValidationDto;
  redactionPolicyVersion: string;
  evalCaseIds: string[];
  finalArtifactRefs: string[];
};

export const VCB_AGENT_EVAL_FAMILIES = [
  'tool_path',
  'blocked_answer',
  'wrong_tool_prevention',
  'numeric_provenance',
  'redaction',
  'pressure_test',
  'autonomy_safety',
] as const;

export type VcbAgentEvalFamilyDto = (typeof VCB_AGENT_EVAL_FAMILIES)[number];

export type VcbAgentEvalCaseDto = {
  evalCaseId: string;
  family: VcbAgentEvalFamilyDto;
  title: string;
  inputRef: string;
  expectedBehavior: string;
  requiredToolIds: string[];
  forbiddenToolIds: string[];
  status: 'not_run' | 'passing' | 'failing' | 'needs_review';
  lastRunAt: string | null;
};

export const VCB_AUTONOMY_LEVELS = [
  {
    level: 1,
    key: 'observe_reconcile_governed_evidence',
    requiresHumanApproval: false,
  },
  {
    level: 2,
    key: 'synthesize_recommendation_with_gaps',
    requiresHumanApproval: false,
  },
  {
    level: 3,
    key: 'draft_decision_pack',
    requiresHumanApproval: false,
  },
  {
    level: 4,
    key: 'capture_human_supervision',
    requiresHumanApproval: true,
  },
  {
    level: 5,
    key: 'submit_to_decision_ledger_for_approval',
    requiresHumanApproval: true,
  },
  {
    level: 6,
    key: 'monitor_approved_commitments',
    requiresHumanApproval: false,
  },
  {
    level: 7,
    key: 'propose_amendments_on_drift',
    requiresHumanApproval: true,
  },
  {
    level: 8,
    key: 'automate_low_risk_bounded_actions',
    requiresHumanApproval: true,
  },
  {
    level: 9,
    key: 'board_grade_financial_hr_reorg_strategy_commitments',
    requiresHumanApproval: true,
  },
] as const;

export type VcbAutonomyLevelDto = (typeof VCB_AUTONOMY_LEVELS)[number];
